import { useMemo, useRef, useState, useEffect } from 'react';
import { ResponsiveGridLayout } from 'react-grid-layout';
import type { Layout } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';

export interface DashboardGridItem {
  i: string;
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface DashboardGridProps {
  layout: DashboardGridItem[];
  children: React.ReactNode;
  cols?: number;
  rowHeight?: number;
  editable?: boolean;
  onLayoutChange?: (layout: DashboardGridItem[]) => void;
}

export function DashboardGrid({ layout, children, cols = 12, rowHeight = 80, editable = false, onLayoutChange }: DashboardGridProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(1200);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setWidth(entry.contentRect.width);
      }
    });
    observer.observe(el);
    setWidth(el.clientWidth);

    return () => observer.disconnect();
  }, []);

  // Note: do NOT set `static` on items — in react-grid-layout v2, static items
  // are skipped by compaction and stay at their declared y position. This causes
  // widgets with high y values (e.g. y:100 used for "append at bottom") to render
  // off-screen. Drag/resize is already controlled by dragConfig/resizeConfig.
  const gridLayout: Layout = useMemo(
    () => layout.map((item) => ({ ...item })),
    [layout],
  );

  const layouts = useMemo(
    () => ({ lg: gridLayout, md: gridLayout, sm: gridLayout }),
    [gridLayout],
  );

  return (
    <div ref={containerRef}>
      <ResponsiveGridLayout
        width={width}
        layouts={layouts}
        breakpoints={{ lg: 1200, md: 996, sm: 768 }}
        cols={{ lg: cols, md: cols, sm: 1 }}
        rowHeight={rowHeight}
        margin={[16, 16]}
        containerPadding={[0, 0]}
        dragConfig={{ enabled: editable }}
        resizeConfig={{ enabled: editable }}
        onLayoutChange={(currentLayout, _allLayouts) => {
          if (editable && onLayoutChange) {
            onLayoutChange(currentLayout.map(({ i, x, y, w, h }) => ({ i, x, y, w, h })));
          }
        }}
      >
        {children}
      </ResponsiveGridLayout>
    </div>
  );
}
